import React, {useEffect, useState} from 'react'
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import Select from 'react-select'
import {DateInput} from '../../../../../components/inputs/DateInput'
import {clearFilters, saveFilters, shouldDisplayFonctionnalite} from '../../../../../components/helpers'
import {KTSVG} from '../../../../../_metronic/helpers'
import {mergeWith, union} from 'lodash'

type StatusMode = 'DEMANDE' | 'PIECE'

const FilterPopper = (props) => {
  const {
    clientDemandeEtape,
    clientDemandeCanal,
    clientDemandeEquipe,
    dateDebut,
    setDateDebut,
    dateFin,
    setDateFin,
    formik,
    updateFilter,
    filters,
    openFilter,
    setOpenFilter,
    setOpenFavoris,
    applyFilter = () => {},
    sortConfig,
    setSortConfig = () => {},
    onSave,
    setOnSave,
    setOnSubmit,
    onSubmit,
    filterTitle,
    setFilterTitle,
    handleFilterSave = () => {},
    handleFavoriFilterUpdate = () => {},
    currentUser,
    setShareWithTeam,
    shareWithTeam,
    allowedToManageFavori,
    favoris,
    setFavoris,
    showFavori,
    setShowFavori,
    disableEdit,
    setBaseline,
    setFilters,
    tempApplyFilter = () => {},
    manualFilters,
    setManualFilters,
    disableButton,
    setDisableButton,
    statusMode,
    setStatusMode,
    TRI_KEYS_TABLE_HEADER,
    setSortField,
    setSortOrder,
  } = props
  const [selectedShareWithTeam, setSelectedShareWithTeam] = useState<any>([])
  const [onValidate, setOnValidate] = useState<boolean>(false)
  const [piecesStatusOpt, setPiecesStatusOpt] = useState<any>([])
  const statusOptions = statusMode === 'DEMANDE' ? clientDemandeEtape : piecesStatusOpt

  const STATUS_CHOICES: {value: StatusMode; label: string}[] = [
    {value: 'DEMANDE', label: 'Demande'},
    {value: 'PIECE', label: 'Pièce'},
  ]


  // --- Popper "Filtrer" ---
  const {
    refs: filterRefs,
    floatingStyles: filterFloatingStyles,
    context: filterContext,
  } = useFloating({
    open: openFilter,
    onOpenChange: setOpenFilter,
    placement: 'bottom-start',
    middleware: [offset(6), flip(), shift()],
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, {
        animationFrame: true,
      }),
  })

  const filterDismiss = useDismiss(filterContext, {
    outsidePressEvent: 'mousedown',
  })

  const {getReferenceProps: getFilterReferenceProps, getFloatingProps: getFilterFloatingProps} =
    useInteractions([filterDismiss])

  useEffect(() => {
    if (openFilter) {
      window.dispatchEvent(new Event('resize'))
    }
  }, [openFilter])

  useEffect(function dateUpdateFilter() {
    updateFilter('dateDebut', dateDebut)
    updateFilter('dateFin', dateFin)
  }, [dateDebut, dateFin])

  const demandeOptions = [
    ...(shouldDisplayFonctionnalite(currentUser, 'VOIR TOUTE DEMANDE')) ?
    [{value: true, label: 'Toutes les demandes'}] : [],
    {value: false, label: 'Mes demandes'},
  ]

  const handleFilterOpening = () => {
    setOnSave(false) // Pour empêcher de tomber sur l'affichage d'insertion libelle
    setOpenFavoris(false) // Ferme l'autre popper
    setOpenFilter((prev) => !prev)
  }

  const handleGoBackAndReset = (onSave) => {
    if (onSave) {
      setOnSave(!onSave)
      setOnSubmit(false)
      setFilterTitle('')
    } else {
      // Si Favori Active, mettre ses données en application
      const isActif = favoris?.filter((fav) => fav?.actif)

      clearFilters() // Réinitialisation donnée filtre temporaire

      if (isActif?.[0]) {
        applyFilter(isActif?.[0])
      } else {
        // Sinon filtre par défaut
        applyFilter({
          id: null,
          filterData: {
            allDemandes: shouldDisplayFonctionnalite(currentUser, 'VOIR TOUTE DEMANDE'),
            statut: [],
            source: [],
            dateDebut: null,
            dateFin: null,
            equipes: [],
            tri: {key: 'ID_DEMANDE', order: 'ASC'},
            shareWithTeam: [],
            statusMode: 'DEMANDE'
          },
        })
      }
      setDisableButton(false)
    }
  }

  // Validation final de la modification de filtre
  const validateEdition = () => {
    if (!onValidate) return

    const {id, ...filtersData} = filters // Suppression du clé id

    const result = {id, filterData: filtersData}
    applyFilter(result)
    // getClientDemandeData(filters)
    if (result?.id) {
      // Màj et activation si favori existant
      handleFavoriFilterUpdate(result?.id, result?.filterData)
    }
    setBaseline(filters)
    setOpenFilter(false)
  }

  // Fonction de fusion entre deux objets
  const mergeFilters = (a, b) => {
    return mergeWith({}, a, b, (objValue, srcValue, key) => {
      if (Array.isArray(objValue) && Array.isArray(srcValue)) {
        return union(srcValue, objValue)
      }
      if (key === 'tri') {
        return srcValue
      }
    })
  }

  const handleFilterEdition = (filters, favoris) => {
    setFavoris((prev) =>
      prev.map((item) => ({
        ...item,
        actif: item.id === favoris?.id,
      }))
    )
    const nextFilters = mergeFilters(favoris?.filterData, manualFilters)

    setFavoris((prev) =>
      prev.map((item) => ({
        ...item,
        actif: item.id === favoris?.id,
      }))
    )


    setFilters(nextFilters)
    setBaseline(nextFilters)
    setShareWithTeam(favoris?.filterData?.shareWithTeam?.length > 0)
    // Si les dates sont null ou vide, prendre celui du favori sinon, prendre celui du state des deux dates
    setDateDebut((dateDebut == null || dateDebut == "") ? favoris?.filterData?.dateDebut : dateDebut)
    setDateFin((dateFin == null || dateFin == "") ? favoris?.filterData?.dateFin : dateFin)
    setStatusMode(favoris?.filterData?.statusMode ?? 'DEMANDE')
    if (TRI_KEYS_TABLE_HEADER?.includes(favoris?.filterData?.tri?.key)) {
      setSortField(favoris?.filterData?.tri?.key)
      setSortOrder(favoris?.filterData?.tri?.order)
      setSortConfig({
        key: null,
        order: 'ASC',
      })
    } else {
      setSortField(null)
      setSortOrder('asc')
      setSortConfig({
        key: favoris?.filterData?.tri?.key ?? null,
        order: favoris?.filterData?.tri?.order ?? 'ASC',
      })
    }
  }

  useEffect(function checkActivatedFavori(){
    const isActif = favoris?.filter((fav) => fav?.actif)

    if (isActif?.length != 0) {
      setFilters((prev) => ({
        ...prev,
        id: isActif?.[0]?.id,
      }))
      setOnValidate(true)
    } else {
      setOnValidate(false)
    }
  }, [favoris])

  const handleFilterApplication = (filters) => {
    const {id, ...filtersData} = filters // Suppression du clé id

    const result = {id, filterData: filtersData}
    tempApplyFilter(result)
    saveFilters(filters)
    setOpenFilter(false)
  }

  useEffect(
    function initOnSubmitIfPopperClosed() {
      if (!openFilter) {
        setOnSubmit(false)
        setOnSave(false)
        setFilterTitle('')
        // setShowFavori(false)
      }
    },
    [openFilter]
  )

  const handleEquipeChange = (e) => {
    const selectedTeam = e ? e.map((opt) => opt.value) : []
    setSelectedShareWithTeam(selectedTeam)
    updateFilter('equipes', selectedTeam)
    if (shareWithTeam) {
      // Si non autorisée alors mettre tableau vide
      updateFilter('shareWithTeam', allowedToManageFavori ? selectedTeam : [])
    }
  }

  useEffect(
    function purgeSharedToTeam() {
      if (shareWithTeam) {
        if (JSON.stringify(filters?.equipes) !== JSON.stringify(filters?.shareWithTeam)) {
          updateFilter('shareWithTeam', filters?.equipes || [])
        }
      } else {
        if (filters?.shareWithTeam && filters?.shareWithTeam.length > 0) {
          updateFilter('shareWithTeam', [])
        }
      }

      if (filters?.equipes?.length === 0 && shareWithTeam) {
        setShareWithTeam(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shareWithTeam, JSON.stringify(filters?.equipes), JSON.stringify(filters?.shareWithTeam)]
  )

  useEffect(
    function filterStatusForPieceOption() {
      if (clientDemandeEtape?.length !== 0) {
        const baseOpt = (clientDemandeEtape ?? []).filter(
          (etape) => !['NOUVE', 'ASSIG', 'MODIF', 'ECHEC'].includes(etape.value)
        )
        const statusOpt = [...baseOpt, {value: 'APROPO', label: 'A proposer'}]
        setPiecesStatusOpt(statusOpt)
      }
    },
    [clientDemandeEtape]
  )

  const handleStatusModeChange = (value) => {
    updateFilter('statusMode', value)
    updateFilter('statut', [])
    setStatusMode(value)
  }

  const renderStatutChoice = () => (
    <>
      {STATUS_CHOICES.map((choice) => (
        <div
          key={choice.value}
          className='form-check form-check-custom form-check-solid form-check-sm justify-content-between p-2 bg-hover-light-primary rounded-1'
        >
          <div className='d-flex'>
            <input
              className='form-check-input me-2'
              type='radio'
              id={`opt-status-mode-${choice.value}`}
              name='opt-status-mode'
              value={choice.value}
              checked={statusMode === choice.value}
              onChange={() => handleStatusModeChange(choice.value)}
            />
            <label
              className='form-check-label'
              htmlFor={`opt-status-mode-${choice.value}`}
            >
              {choice.label}
            </label>
          </div>
        </div>
      ))}
    </>
  )


  const renderFilterForm = () => {
    return (
      <>
        <div className='d-flex align-items-center mb-2'>
          {/*<div className='form-check form-check-custom form-check-solid form-check-sm me-2'>*/}
          {/*  <input className='form-check-input' type='checkbox' value='' id='flexRadioLg' />*/}
          {/*</div>*/}
          <div style={{width: '150px'}}>Demandes</div>
          <Select
            className='react-select-styled react-select-solid react-select-lg flex-fill'
            classNamePrefix='react-select'
            options={demandeOptions}
            fieldName='allDemandes'
            value={demandeOptions?.find((dmd) => dmd?.value == filters?.allDemandes) ?? null}
            onChange={(e) => updateFilter('allDemandes', e.value)}
            menuPortalTarget={document.body}
            menuPosition='fixed'
            styles={{
              menuPortal: (base) => ({...base, zIndex: 9999}),
              container: (base) => ({...base, width: '250px'}),
            }}
            placeholder={'Séléctionner demande'}
            style={{maxWidth: '277px'}}
          />
        </div>

        <div className='d-flex align-items-center mb-2'>
          <div style={{width: '150px'}}></div>
          {renderStatutChoice()}
        </div>

        <div className='d-flex align-items-center mb-2'>
          {/*<div className='form-check form-check-custom form-check-solid form-check-sm me-2'>*/}
          {/*  <input className='form-check-input' type='checkbox' value='' id='flexRadioLg' />*/}
          {/*</div>*/}
          <div style={{width: '150px'}}>Statut</div>
          <Select
            className='react-select-styled react-select-solid react-select-lg flex-fill'
            classNamePrefix='react-select'
            options={statusOptions}
            fieldName='statut'
            value={statusOptions?.filter((etape) => filters?.statut?.includes(etape?.value))}
            onChange={(e) => updateFilter('statut', e ? e.map((opt) => opt.value) : [])}
            menuPortalTarget={document.body}
            menuPosition='fixed'
            styles={{
              menuPortal: (base) => ({...base, zIndex: 9999}),
              container: (base) => ({...base, width: '250px'}),
            }}
            placeholder={'Séléctionner statut'}
            isMulti
            style={{maxWidth: '277px'}}
          />
        </div>

        <div className='d-flex align-items-center mb-2'>
          {/*<div className='form-check form-check-custom form-check-solid form-check-sm me-2'>*/}
          {/*  <input className='form-check-input' type='checkbox' value='' id='flexRadioLg' />*/}
          {/*</div>*/}
          <div style={{width: '150px'}}>Canal</div>
          <Select
            className='react-select-styled react-select-solid react-select-lg flex-fill'
            classNamePrefix='react-select'
            options={clientDemandeCanal}
            fieldName='source'
            value={clientDemandeCanal?.filter((source) => filters?.source?.includes(source?.value))}
            onChange={(e) => updateFilter('source', e ? e.map((opt) => opt.value) : [])}
            menuPortalTarget={document.body}
            menuPosition='fixed'
            styles={{
              menuPortal: (base) => ({...base, zIndex: 9999}),
              container: (base) => ({...base, width: '250px'}),
            }}
            placeholder={'Séléctionner canal'}
            isMulti
            style={{maxWidth: '277px'}}
          />
        </div>

        <div className='d-flex align-items-center mb-2'>
          {/*<div className='form-check form-check-custom form-check-solid form-check-sm me-2'>*/}
          {/*  <input className='form-check-input' type='checkbox' value='' id='flexRadioLg' />*/}
          {/*</div>*/}
          <div style={{width: '150px'}}>Equipe</div>
          <div style={{width: '277px'}}>
            <Select
              className='react-select-styled react-select-solid react-select-lg w-100'
              classNamePrefix='react-select'
              options={clientDemandeEquipe}
              fieldName='equipe'
              value={clientDemandeEquipe?.filter((equipe) =>
                filters?.equipes?.includes(equipe?.value)
              )}
              onChange={(e) => handleEquipeChange(e)}
              menuPortalTarget={document.body}
              menuPosition='fixed'
              styles={{
                menuPortal: (base) => ({...base, zIndex: 9999}),
                container: (base) => ({...base, width: '250px'}),
              }}
              placeholder={'Séléctionner equipe'}
              isMulti
            />
            {allowedToManageFavori && (
              <div className='form-check form-check-custom form-check-solid form-check-sm my-3'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  checked={shareWithTeam}
                  id='flexRadioLg'
                  onChange={() => setShareWithTeam(!shareWithTeam)}
                  disabled={filters?.equipes?.length === 0}
                />
                <label className='form-check-label' htmlFor='flexRadioLg'>
                  Partager avec l'équipe
                </label>
              </div>
            )}
          </div>
        </div>

        <div className='d-flex align-items-center mb-2'>
          {/*<div className='form-check form-check-custom form-check-solid form-check-sm me-2'>*/}
          {/*  <input className='form-check-input' type='checkbox' value='' id='flexRadioLg' />*/}
          {/*</div>*/}
          <div style={{width: '150px'}}>Date début</div>
          <div className={'flex-grow-1'}>
            <DateInput
              fieldName='dateDebut'
              inputClassName='form-control input-date-grp-debut h-40px'
              value={dateDebut}
              setter={setDateDebut}
              formik={formik}
              placeholder='Date début'
              customHeight={true}
              height={40}
            />
          </div>
        </div>

        <div className='d-flex align-items-center mb-2'>
          {/*<div className='form-check form-check-custom form-check-solid form-check-sm me-2'>*/}
          {/*  <input className='form-check-input' type='checkbox' value='' id='flexRadioLg' />*/}
          {/*</div>*/}
          <div style={{width: '150px'}}>Date fin</div>
          <div className={'flex-grow-1'}>
            <DateInput
              fieldName='dateFin'
              inputClassName='form-control input-date-grp-fin h-40px'
              value={dateFin}
              setter={setDateFin}
              formik={formik}
              placeholder='Date fin'
              customHeight={true}
              height={40}
            />
          </div>
        </div>
        {/*Marquer comme libelle pré-etablie*/}
        {/*{allowedToManageFavori && (*/}
        {/*  <>*/}
        {/*    <hr className={'flex-grow-1 text-muted w-100'} />*/}

        {/*    <div className='form-check form-check-custom form-check-solid form-check-sm mb-3'>*/}
        {/*      <input*/}
        {/*        className='form-check-input'*/}
        {/*        type='checkbox'*/}
        {/*        checked={shareWithTeam}*/}
        {/*        id='flexRadioLg'*/}
        {/*        onChange={() => setShareWithTeam(!shareWithTeam)}*/}
        {/*      />*/}
        {/*      <label className='form-check-label' htmlFor='flexRadioLg'>*/}
        {/*        Partager avec l'équipe*/}
        {/*      </label>*/}
        {/*    </div>*/}
        {/*    {shareWithTeam && (*/}
        {/*      <Select*/}
        {/*        className='react-select-styled react-select-solid react-select-lg flex-fill mb-3 w-100'*/}
        {/*        classNamePrefix='react-select'*/}
        {/*        options={clientDemandeEquipe}*/}
        {/*        fieldName='equipe'*/}
        {/*        value={clientDemandeEquipe?.filter((equipe) =>*/}
        {/*          filters?.equipe?.includes(equipe?.value)*/}
        {/*        )}*/}
        {/*        // onChange={(selectedOptions) =>*/}
        {/*        //   addTeamOnFavori(*/}
        {/*        //     selectedOptions ? selectedOptions.map((opt) => opt.value) : []*/}
        {/*        //   )*/}
        {/*        // }*/}
        {/*        menuPortalTarget={document.body}*/}
        {/*        menuPosition='fixed'*/}
        {/*        styles={{*/}
        {/*          menuPortal: (base) => ({...base, zIndex: 9999}),*/}
        {/*          container: (base) => ({...base, width: '250px'}),*/}
        {/*        }}*/}
        {/*        placeholder={'Séléctionner equipe'}*/}
        {/*        isMulti*/}
        {/*        required={shareWithTeam}*/}
        {/*      />*/}
        {/*    )}*/}
        {/*  </>*/}
        {/*)}*/}
      </>
    )
  }

  const renderSortForm = () => {
    const triOptions = [
      {value: 'STATUT', label: 'Statut'},
      {value: 'ASSIGN', label: 'Assigné à'},
      {value: 'EQUIPE', label: 'Equipe'},
      {value: 'CANAL', label: 'Canal'},
      {value: 'ID_DEMANDE', label: 'Numéro de demande'},
      {value: 'DELAI', label: 'Délai'},
    ]

    const handleSortPosition = (key) => {
      const sorting = {
        key,
        order: sortConfig.key === key && sortConfig.order === 'ASC' ? 'DESC' : 'ASC',
      }
      setSortConfig(sorting)
      setSortField(null)
      setSortOrder('asc')
      updateFilter('tri', sorting)
    }

    const handleResetTri = () => {
      const sorting = {
        key: null,
        order: null,
      }
      setSortConfig(sorting)
      setSortField(null)
      setSortOrder('asc')
      updateFilter('tri', sorting)
    }

    return (
      <div className='d-flex flex-column align-items-start mb-2 gap-3 border-start h-100 py-3 px-6'>
        <span className={'fw-bold text-start fs-5 w-100'}>
          <div className={'d-flex justify-content-between align-items-center'} style={{ width: '160px' }}>
            <span>Trier par:</span>
            <span
              className={'btn btn-icon btn-bg-light btn-sm btn-light-warning'}
              onClick={() => handleResetTri()}
              // data-tooltip-id='tooltip-filter'
              // data-tooltip-content={'Réinitialiser le tri'}
              // data-tooltip-place='top'
            >
              <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 32 32'>
                <path d='M0 0h32v32H0z' fill='none' />
                <path
                  fill='currentColor'
                  d='M8.707 3.293a1 1 0 0 1 0 1.414L5.414 8H17.5C23.299 8 28 12.701 28 18.5S23.299 29 17.5 29S7 24.299 7 18.5a1 1 0 1 1 2 0a8.5 8.5 0 1 0 8.5-8.5H5.414l3.293 3.293a1 1 0 1 1-1.414 1.414l-5-5a1 1 0 0 1 0-1.414l5-5a1 1 0 0 1 1.414 0'
                />
              </svg>
            </span>
          </div>
        </span>
        {triOptions.map((item, index) => (
          <div key={index} className={'d-flex gap-3 align-items-center'}>
            <label
              className={`cursor-pointer text-hover-primary ${
                sortConfig.key === item.value ? 'text-primary' : ''
              }`}
              style={{width: '130px'}}
              onClick={() => handleSortPosition(item.value)}
            >
              {item.label}
            </label>
            {sortConfig.key === item.value && (
              <i
                className={`fs-3 bi ${
                  sortConfig.order === 'ASC' ? 'bi-caret-up-fill' : 'bi-caret-down-fill'
                } cursor-pointer`}
                onClick={() => handleSortPosition(item.value)}
              ></i>
            )}
          </div>
        ))}
      </div>
    )
  }

  const favorisEquipes = favoris?.filter((fav) => {
    const userEquipeIds = Array.isArray(clientDemandeEquipe)
      ? clientDemandeEquipe.map((e: any) => e?.value)
      : []

    return (
      fav?.filterData?.shareWithTeam?.length > 0 &&
      fav.filterData.shareWithTeam.some((equipeId: number) => userEquipeIds.includes(equipeId))
    )
  })

  const favorisPerso = favoris?.filter((fav) => {
    const shareWithTeam = fav?.filterData?.shareWithTeam

    return !shareWithTeam || shareWithTeam.length === 0
  })

  useEffect(function dataSetterForManualInsertion() {
    setManualFilters((prev) => ({
      ...prev,
        dateDebut: dateDebut,
        dateFin: dateFin,
    }))
  }, [dateDebut, dateFin])

  const handleUserManualFilter = () => {
    setManualFilters(filters)
    setShowFavori(true)
  }

  const renderFavoriInput = (favori) => {
    return (
      <div key={favori?.id}>
        <div className='form-check form-check-custom form-check-solid form-check-sm justify-content-between p-2 bg-hover-light-primary rounded-1'>
          <div className={'d-flex'}>
            <input
              className='form-check-input me-2'
              type='radio'
              id={`option-${favori?.id}`}
              name={'opt-fav'}
              checked={!!favori?.actif}
              onClick={() => handleFilterEdition(filters, favori)}
              onChange={() => {}}
            />
            <label className='form-check-label' htmlFor={`option-${favori?.id}`}>
              {favori?.libelle}
            </label>
          </div>
        </div>
      </div>
    )
  }
  // Si changement des inputs du filtres
  // Cas 1: favoris Activé, ne pas montré Favoris
  // Cas 2: favoris non activé, montré Favoris avec demande choix du favoris à activer
  const renderFavori = () => {
    // if (!showFavori) return

    return (
      <div className={'d-flex gap-2 flex-column mh-300px w-200px overflow-y-scroll'}>
        <>
          <p className={'text-small fw-semibold mb-0'}>Favori de l'equipe</p>
          {favorisEquipes?.length > 0 ? (
            favorisEquipes?.map((fav) => renderFavoriInput(fav))
          ) : (
            <p className={'text-muted text-center'}>(Aucun Favori créé)</p>
          )}
          <hr className={'flex-grow-1 text-muted w-100 my-1'} />
          <p className={'text-small fw-semibold mb-0'}>Favori personnalisé</p>
          {favorisPerso?.length > 0 ? (
            favorisPerso.map((fav) => renderFavoriInput(fav))
          ) : (
            <p className='text-muted text-center'>(Aucun Favori créé)</p>
          )}
        </>
      </div>
    )
  }

  return (
    <>
      <button
        className={'btn btn-sm btn-light-info flex-fill py-2'}
        ref={filterRefs.setReference}
        {...getFilterReferenceProps()}
        onClick={handleFilterOpening}
      >
        <div className={'d-flex justify-content-between align-items-center'}>
          <i className='ki-outline ki-filter fs-2'></i>
          <label className={'fs-6'}>Filtrer</label>
          <i className={`fs-2 ki-outline ki-${openFilter ? 'up' : 'down'}`}></i>
        </div>
      </button>

      {openFilter && (
        <FloatingPortal>
          <div
            className='p-6 shadow bg-white p-2 rounded d-flex flex-column gap-1 align-items-center'
            ref={filterRefs.setFloating}
            style={{...filterFloatingStyles, zIndex: '9999'}}
            {...getFilterFloatingProps()}
          >
            <div className={'d-flex w-100 align-items-center'}>
              <div className={'fw-bold text-start fs-5 w-100'}>
                Choisissez vos Filtres
                {/*Seulement si aucun filtre sélectionné + modification filtre*/}
                {favoris?.length > 0 && !onSubmit && !disableEdit && !onValidate && (
                  <div className={'d-flex gap-2 align-items-center mb-0 mt-1'}>
                    <i className='bi bi-info-square text-danger'></i>
                    <span className={'text-danger fs-6 fw-normal'}>
                      Veuillez sélectionner un favori à modifier.
                    </span>
                  </div>
                )}
              </div>
              <div
                className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                aria-label='Close'
                onClick={() => setOpenFilter(false)}
              >
                <KTSVG
                  path='/media/icons/duotune/arrows/arr061.svg'
                  className='svg-icon svg-icon-2x'
                />
              </div>
            </div>

            <hr className={'flex-grow-1 text-muted w-100'} />
            <form onSubmit={handleFilterSave}>
              {onSave ? (
                <div className={'d-flex align-items-center mb-2'}>
                  <label className='form-label'>Insérer le nom de votre Filtre</label>
                  <input
                    type='text'
                    className='form-control'
                    onChange={(e) => setFilterTitle(e.target.value)}
                    value={filterTitle}
                    required={true}
                  />
                </div>
              ) : (
                <div className={'d-flex gap-6'}>
                  <div>{renderFavori()}</div>
                  <div>{renderFilterForm()}</div>
                  <div>{renderSortForm()}</div>
                </div>
              )}

              <hr className={'flex-grow-1 text-muted w-100'} />

              <div className={'d-flex justify-content-between'}>
                <button
                  className={`btn btn-sm btn-${onSave ? 'secondary' : 'danger'}`}
                  type={'button'}
                  onClick={() => handleGoBackAndReset(onSave)}
                >
                  {onSave ? 'Retour' : 'Réinitialiser'}
                </button>

                <div>
                  <button
                    type='button'
                    className='btn btn-sm btn-secondary mx-3'
                    onClick={() => setOpenFilter(false)}
                  >
                    Fermer
                  </button>
                  {!onSubmit && onValidate && disableButton && (
                    <button
                      type='button'
                      className='btn btn-sm btn-warning mx-3'
                      onClick={() => (onValidate ? validateEdition() : handleUserManualFilter())}
                    >
                      Modifier
                    </button>
                  )}
                  <div className='btn-group btn-group-sm' role='group' aria-label='First group'>
                    <button
                      type='button'
                      className='btn btn-info'
                      onClick={() => handleFilterApplication(filters)}
                    >
                      Appliquer
                    </button>
                    {onSubmit ? (
                      <button type='submit' className='btn btn-primary'>
                        Sauvegarder et appliquer
                      </button>
                    ) : (
                      <button
                        className={'btn btn-sm btn-primary'}
                        type={'button'}
                        onClick={() => {
                          setOnSave(true)
                          setOnSubmit(true)
                        }}
                      >
                        Créer
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </form>
          </div>
        </FloatingPortal>
      )}
      {/*<Tooltip id={"tooltip-filter"} />*/}
    </>
  )
}


export default FilterPopper